// Global search box in the top bar — 2026-09-06. Cmd/Ctrl+K focuses it; Esc closes.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Spin, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { globalSearch, GlobalSearchGroup, GlobalSearchHit } from '../api';

const { Text } = Typography;
const COLORS: Record<string, string> = { mr: 'blue', po: 'purple', grn: 'cyan', vendor: 'orange', catalog: 'geekblue', costCenter: 'green', user: 'default', department: 'default' };

const GlobalSearch: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [groups, setGroups] = useState<GlobalSearchGroup[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<any>(null);
  const seq = useRef(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') { e.preventDefault(); inputRef.current?.focus(); setOpen(true); }
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('mousedown', onClick); };
  }, []);

  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) { setGroups([]); setLoading(false); return; }
    setLoading(true);
    const my = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const res = await globalSearch(text);
        if (my === seq.current) { setGroups(res.groups); setActive(0); }
      } catch { if (my === seq.current) setGroups([]); }
      finally { if (my === seq.current) setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const flat: GlobalSearchHit[] = groups.flatMap((g) => g.items);

  const go = useCallback((hit: GlobalSearchHit) => {
    setOpen(false);
    if (!hit.url) return;
    try {
      // Department / person results open the MR list pre-filtered (the list reads its filters from sessionStorage).
      if ((hit.type === 'department' || hit.type === 'user') && hit.meta?.departmentId) {
        sessionStorage.setItem('list.mrs', JSON.stringify({ depts: [hit.meta.departmentId], page: 1, statuses: [], dateRange: null }));
      }
      if (hit.type === 'catalog' && hit.meta?.search) sessionStorage.setItem('catalog.search', hit.meta.search);
    } catch { /* ignore */ }
    navigate(hit.url);
  }, [navigate]);

  return (
    <div ref={boxRef} style={{ position: 'relative', width: compact ? 180 : 360 }}>
      <Input
        ref={inputRef}
        allowClear
        prefix={<SearchOutlined style={{ color: '#999' }} />}
        placeholder={compact ? 'Search' : 'Search MRs, POs, vendors, parts, cost centers…  ⌘/'}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          if (e.key === 'Enter' && flat[active]) go(flat[active]);
        }}
      />
      {open && q.trim().length >= 2 && (
        <div style={{ ...(compact ? { position: 'fixed' as const, top: 60, left: 8, right: 8, maxWidth: 560, margin: '0 auto', maxHeight: 'calc(100vh - 160px)' } : { position: 'absolute' as const, top: 36, left: 0, width: 560, maxHeight: 520 }), overflowY: 'auto', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,.12)', zIndex: 1100, padding: 6 }}>
          {loading && flat.length === 0 && <div style={{ padding: 12, textAlign: 'center' }}><Spin size="small" /></div>}
          {!loading && flat.length === 0 && <div style={{ padding: 12 }}><Text type="secondary">No matches</Text></div>}
          {groups.map((g) => (
            <div key={g.type} style={{ marginBottom: 6 }}>
              <div style={{ padding: '4px 8px', fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>{g.label}</div>
              {g.items.map((it) => {
                const idx = flat.indexOf(it);
                return (
                  <div
                    key={`${it.type}-${it.id}`}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => go(it)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, cursor: it.url ? 'pointer' : 'default', background: idx === active ? '#f0f5ff' : 'transparent' }}
                  >
                    <Tag color={COLORS[it.type] || 'default'} style={{ margin: 0, minWidth: 40, textAlign: 'center' }}>{it.type === 'costCenter' ? 'CC' : it.type.toUpperCase()}</Tag>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title}</div>
                      {it.subtitle && <div style={{ fontSize: 12, color: '#777', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.subtitle}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
